import React, { useState } from "react";
import { COMPONENT_TYPES } from "../../../../pages/constants/AiAgent";
import { ComponentActionButton } from "./ComponentActionButton";
import { useComponentAction } from "./useComponentAction";
import { HiOutlineEnvelope } from "react-icons/hi2";

const getStatusColor = (status) => {
  const s = status?.toLowerCase() || "";
  if (s === "applied") return "bg-blue-100 text-blue-700";
  if (s === "shortlisted" || s === "selected") return "bg-green-100 text-green-700";
  if (s === "rejected") return "bg-red-100 text-red-700";
  if (s === "hired") return "bg-purple-100 text-purple-700";
  if (s === "pending") return "bg-yellow-100 text-yellow-700";
  if (s.startsWith("interviewed")) return "bg-indigo-100 text-indigo-700";
  if (s.includes("offer")) return "bg-emerald-100 text-emerald-700";
  if (s.includes("acceptance")) return "bg-orange-100 text-orange-700";
  return "bg-gray-100 text-gray-700";
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${getStatusColor(status)}`}>
      {status}
    </span>
  );
};

const CandidateCardCompact = ({ candidate, onAction, actionsDisabled }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-white hover:border-blue-300 transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-primaryBlack truncate">
            {candidate.index}. {candidate.name}
          </p>
          <StatusBadge status={candidate.status} />
        </div>
        {candidate.profile_name && (
          <p className="text-xs text-blue-600 mt-0.5 truncate">
            {candidate.profile_name}
          </p>
        )}
      </div>
      <div className="flex gap-1.5 ml-2 flex-shrink-0">
        {candidate.actions?.map((action) => (
          <ComponentActionButton
            key={action.action}
            {...action}
            onAction={(a, p) => onAction(a, p)}
            disabled={actionsDisabled}
          />
        ))}
      </div>
    </div>
  </div>
);

const CandidateCardMidDetail = ({ candidate, onAction, actionsDisabled }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-white hover:border-blue-300 transition-colors">
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-primaryBlack">
            {candidate.index}. {candidate.name}
          </p>
          <StatusBadge status={candidate.status} />
        </div>
        {candidate.profile_name && (
          <p className="text-xs text-blue-600 mt-0.5">
            {candidate.profile_name}
          </p>
        )}
        {candidate.email && (
          <div className="flex items-center gap-1 mt-1.5 text-xs text-textGray">
            <HiOutlineEnvelope className="w-3 h-3" /> {candidate.email}
          </div>
        )}
      </div>
    </div>
    <div className="flex gap-1.5 mt-2">
      {candidate.actions?.map((action) => (
        <ComponentActionButton
          key={action.action}
          {...action}
          onAction={(a, p) => onAction(a, p)}
          disabled={actionsDisabled}
        />
      ))}
    </div>
  </div>
);

const variantCardMap = {
  compact: CandidateCardCompact,
  mid_detail: CandidateCardMidDetail,
};

const CandidateList = ({ payload, disabled }) => {
  const { variant = "mid_detail", data, actions } = payload;
  const { sendAction } = useComponentAction();
  const [actionsDisabled, setActionsDisabled] = useState(disabled);

  const CardComponent = variantCardMap[variant] || CandidateCardMidDetail;

  const handleAction = (action, params) => {
    setActionsDisabled(true);
    sendAction(COMPONENT_TYPES.CANDIDATE_LIST, action, params);
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {data?.candidates?.map((candidate) => (
        <CardComponent
          key={candidate.candidate_id}
          candidate={candidate}
          onAction={handleAction}
          actionsDisabled={actionsDisabled}
        />
      ))}
      {actions?.length > 0 && (
        <div className="flex gap-1.5 mt-1">
          {actions.map((action) => (
            <ComponentActionButton
              key={action.action}
              {...action}
              onAction={handleAction}
              disabled={actionsDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CandidateList;
